import { COMBO } from '../config/GameConfig';

/**
 * ComboSystem —— 连杀计数 + 奖励
 *
 * 状态机：
 *   每次击杀 → bump()：递增 combo、记录时间、回调 onShow(n)；
 *   每帧 → tick()：超过 windowMs 没击杀就 break；
 *   玩家受伤 / 死亡 → forceBreak()：立即结算 + 隐藏 HUD。
 *
 * 副作用通过回调注入，让本类零依赖（无需 import 任何 UI）：
 *   onShow(n)         —— 段数变化时刷新 HUD（n>=2 才显示）
 *   onHide()          —— combo 断时隐藏 HUD
 *   onCoinReward()    —— 每达成 N 段奖励 1 金币
 *   onBreakBonus(s)   —— combo 断时给一次 score 奖励（s 已经按 breakBonusMul 算好）
 */
export interface ComboHooks {
  onShow?: (n: number) => void;
  onHide?: () => void;
  onCoinReward?: () => void;
  onBreakBonus?: (score: number) => void;
}

export default class ComboSystem {
  /** 当前 combo 段数 */
  count = 0;
  /** 本局历史最大 combo */
  max = 0;
  /** 上次击杀时间戳（performance.now()）；超过 COMBO.windowMs 视为断 */
  private _lastAt = 0;
  private _hooks: ComboHooks;

  constructor(hooks: ComboHooks = {}) {
    this._hooks = hooks;
  }

  reset(): void {
    this.count = 0;
    this.max = 0;
    this._lastAt = 0;
    this._hooks.onHide?.();
  }

  /** 每帧调一次：超过窗口期就 break */
  tick(): void {
    if (this.count === 0) return;
    if (performance.now() - this._lastAt > COMBO.windowMs) {
      this.forceBreak();
    }
  }

  /** 击杀时调用：递增段数并通知 HUD；满 N 段奖励金币 */
  bump(): void {
    this.count += 1;
    this._lastAt = performance.now();
    if (this.count > this.max) this.max = this.count;
    this._hooks.onShow?.(this.count);
    if (this.count > 0 && this.count % COMBO.rewardEveryN === 0) {
      this._hooks.onCoinReward?.();
    }
  }

  /** 强制断 combo（玩家受击 / 主动重置）：把累计值 × breakBonusMul 给 score 奖励 */
  forceBreak(): void {
    if (this.count <= 0) return;
    const bonus = this.count * COMBO.breakBonusMul;
    if (bonus > 0) this._hooks.onBreakBonus?.(bonus);
    this.count = 0;
    this._hooks.onHide?.();
  }

  /** 当前 combo 是否处于"大段加成"区（用来给伤害计算加 ×bigComboDamageMul） */
  isBig(): boolean {
    return this.count >= COMBO.bigComboThreshold;
  }

  /** 当前命中应该乘的 combo 伤害倍率 */
  damageMul(): number {
    return this.isBig() ? COMBO.bigComboDamageMul : 1;
  }
}
